import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import {
  financePaymentMethodLabel,
  financeTransactionKindLabel,
  loadFinanceManagementSnapshot,
  type FinanceCounterparty,
  type FinanceManagementSnapshot,
  type FinancePaymentMethod,
  type FinanceTransaction,
  type FinanceTransactionKind,
} from "../finance/financeManagement";
import {
  buildFinanceCounterpartyUpsertOperation,
  buildFinancePaymentRecordOperation,
  buildFinanceTransactionRecordOperation,
  buildFinanceTransactionVoidOperation,
} from "../operations/financeOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface FinanceManagementPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

type PendingAction = "counterparty" | "transaction" | "payment" | "void";

const EMPTY_SNAPSHOT: FinanceManagementSnapshot = {
  counterparties: [],
  transactions: [],
  payments: [],
};

const CATEGORY_OPTIONS: Readonly<
  Record<FinanceTransactionKind, readonly { code: string; label: string }[]>
> = {
  sale: [{ code: "fish_sale", label: "فروش ماهی" }],
  purchase: [
    { code: "feed_purchase", label: "خرید غذا" },
    { code: "medicine_purchase", label: "خرید دارو" },
    { code: "supply_purchase", label: "خرید ملزومات" },
    { code: "equipment_asset_purchase", label: "خرید تجهیزات" },
  ],
  expense: [
    { code: "hall_expense", label: "هزینه سالن" },
    { code: "equipment", label: "هزینه تجهیزات" },
  ],
};

function firstRecord(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) {
    const first = value[0];
    return first && typeof first === "object" ? (first as Record<string, unknown>) : undefined;
  }
  return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

function localDateValue(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function amountValue(value: string): number {
  const normalized = value.replace(/[٬,\s]/g, "");
  return Number(normalized);
}

function formatToman(value: number): string {
  return `${new Intl.NumberFormat("fa-IR").format(value)} تومان`;
}

function syncMessage(status: string): string {
  if (status === "synced") return "ثبت مالی با سرور همگام شد.";
  if (status === "pending") return "ثبت مالی در صف آفلاین ذخیره شد.";
  if (status === "retryable_error") return "ثبت مالی حفظ شد و دوباره ارسال می‌شود.";
  if (status === "terminal_conflict")
    return "ثبت مالی با وضعیت فعلی تعارض دارد؛ اطلاعات را تازه‌سازی کنید.";
  return "ثبت مالی ذخیره شد.";
}

function counterpartyName(counterparty: FinanceCounterparty): string {
  return [counterparty.firstName, counterparty.lastName].filter(Boolean).join(" ") || "طرف حساب";
}

export function FinanceManagementPanel({
  farmId,
  userId,
  onSyncSummary,
}: FinanceManagementPanelProps) {
  const [snapshot, setSnapshot] = useState<FinanceManagementSnapshot>(EMPTY_SNAPSHOT);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<OfflineOperation>();
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);

  const [editingCounterparty, setEditingCounterparty] = useState<FinanceCounterparty>();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [isCustomer, setIsCustomer] = useState(true);
  const [isVendor, setIsVendor] = useState(false);
  const [counterpartyActive, setCounterpartyActive] = useState(true);

  const [transactionKind, setTransactionKind] = useState<FinanceTransactionKind>("sale");
  const [categoryCode, setCategoryCode] = useState(CATEGORY_OPTIONS.sale[0]?.code ?? "fish_sale");
  const [description, setDescription] = useState("");
  const [amountToman, setAmountToman] = useState("");
  const [occurredOn, setOccurredOn] = useState(localDateValue);
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [transactionCounterpartyId, setTransactionCounterpartyId] = useState("");
  const [initialPaidAmount, setInitialPaidAmount] = useState("");
  const [initialPaymentMethod, setInitialPaymentMethod] =
    useState<FinancePaymentMethod>("card_transfer");

  const [paymentTarget, setPaymentTarget] = useState<FinanceTransaction>();
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<FinancePaymentMethod>("card_transfer");
  const [paymentDate, setPaymentDate] = useState(localDateValue);
  const [paymentNote, setPaymentNote] = useState("");

  const [voidTarget, setVoidTarget] = useState<FinanceTransaction>();
  const [voidReason, setVoidReason] = useState("");

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const [permissionResult, nextSnapshot] = await Promise.all([
        supabase.rpc("permission_decide", {
          target_farm_id: farmId,
          requested_permission: "finance.write",
        }),
        loadFinanceManagementSnapshot(supabase, farmId),
      ]);
      if (permissionResult.error) throw permissionResult.error;
      setCanWrite(firstRecord(permissionResult.data)?.allowed === true);
      setSnapshot(nextSnapshot);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری حسابداری انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const totals = useMemo(() => {
    const posted = snapshot.transactions.filter((item) => item.status === "posted");
    return {
      sale: posted
        .filter((item) => item.transactionKind === "sale")
        .reduce((sum, item) => sum + item.amountToman, 0),
      purchase: posted
        .filter((item) => item.transactionKind === "purchase")
        .reduce((sum, item) => sum + item.amountToman, 0),
      expense: posted
        .filter((item) => item.transactionKind === "expense")
        .reduce((sum, item) => sum + item.amountToman, 0),
      remaining: posted.reduce((sum, item) => sum + item.remainingAmountToman, 0),
    };
  }, [snapshot.transactions]);

  function operationContext() {
    return {
      actorId: userId,
      farmId,
      deviceId: getOrCreateDeviceId(),
      performedAt: new Date().toISOString(),
    };
  }

  function resetCounterpartyForm() {
    setEditingCounterparty(undefined);
    setFirstName("");
    setLastName("");
    setPhone("");
    setIsCustomer(true);
    setIsVendor(false);
    setCounterpartyActive(true);
  }

  function editCounterparty(counterparty: FinanceCounterparty) {
    setEditingCounterparty(counterparty);
    setFirstName(counterparty.firstName ?? "");
    setLastName(counterparty.lastName ?? "");
    setPhone(counterparty.phone ?? "");
    setIsCustomer(counterparty.isCustomer);
    setIsVendor(counterparty.isVendor);
    setCounterpartyActive(counterparty.isActive);
    setMessage(undefined);
    setError(undefined);
  }

  function resetTransactionForm() {
    setTransactionKind("sale");
    setCategoryCode(CATEGORY_OPTIONS.sale[0]?.code ?? "fish_sale");
    setDescription("");
    setAmountToman("");
    setOccurredOn(localDateValue());
    setQuantity("");
    setUnit("");
    setTransactionCounterpartyId("");
    setInitialPaidAmount("");
    setInitialPaymentMethod("card_transfer");
  }

  function prepareCounterparty() {
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWrite) throw new Error("FINANCE_WRITE_PERMISSION_DENIED");

      const operation = buildFinanceCounterpartyUpsertOperation(operationContext(), {
        counterpartyId: editingCounterparty?.id ?? crypto.randomUUID(),
        expectedVersion: editingCounterparty?.domainVersion ?? 0,
        ...(firstName.trim() ? { firstName } : {}),
        ...(lastName.trim() ? { lastName } : {}),
        ...(phone.trim() ? { phone } : {}),
        isCustomer,
        isVendor,
        isActive: counterpartyActive,
      });

      setPending(operation);
      setPendingAction("counterparty");
      setConfirmationParts([
        editingCounterparty ? "ویرایش طرف حساب" : "طرف حساب جدید",
        `نام: ${[firstName, lastName].filter(Boolean).join(" ") || "بدون نام"}`,
        `تلفن: ${phone.trim() || "ثبت نشده"}`,
        `نوع: ${
          [isCustomer ? "مشتری" : "", isVendor ? "فروشنده" : ""].filter(Boolean).join("، ") ||
          "عمومی"
        }`,
        `وضعیت: ${counterpartyActive ? "فعال" : "غیرفعال"}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات طرف حساب معتبر نیست.");
    }
  }

  function prepareTransaction() {
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWrite) throw new Error("FINANCE_WRITE_PERMISSION_DENIED");

      const amount = amountValue(amountToman);
      const paid = initialPaidAmount.trim() ? amountValue(initialPaidAmount) : 0;
      if (!Number.isFinite(amount) || !Number.isFinite(paid)) {
        throw new Error("FINANCE_AMOUNT_INVALID");
      }

      const operation = buildFinanceTransactionRecordOperation(operationContext(), {
        transactionId: crypto.randomUUID(),
        transactionKind,
        categoryCode,
        description,
        amountToman: amount,
        ...(quantity.trim() ? { quantity: amountValue(quantity) } : {}),
        ...(unit.trim() ? { unit } : {}),
        ...(transactionCounterpartyId ? { counterpartyId: transactionCounterpartyId } : {}),
        occurredOn,
        ...(paid > 0
          ? {
              initialPayment: {
                paymentId: crypto.randomUUID(),
                amountToman: paid,
                paymentMethod: initialPaymentMethod,
              },
            }
          : {}),
      });

      const category =
        CATEGORY_OPTIONS[transactionKind].find((item) => item.code === categoryCode)?.label ??
        categoryCode;
      setPending(operation);
      setPendingAction("transaction");
      setConfirmationParts([
        financeTransactionKindLabel(transactionKind),
        `دسته: ${category}`,
        `شرح: ${description.trim()}`,
        `مبلغ: ${formatToman(amount)}`,
        `پرداخت اولیه: ${paid > 0 ? formatToman(paid) : "ندارد"}`,
        `تاریخ: ${occurredOn}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات ثبت مالی معتبر نیست.");
    }
  }

  function startPayment(transaction: FinanceTransaction) {
    setPaymentTarget(transaction);
    setPaymentAmount(String(transaction.remainingAmountToman));
    setPaymentDate(localDateValue());
    setPaymentNote("");
    setPaymentMethod("card_transfer");
    setVoidTarget(undefined);
    setMessage(undefined);
    setError(undefined);
  }

  function preparePayment() {
    if (!paymentTarget) return;
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWrite) throw new Error("FINANCE_WRITE_PERMISSION_DENIED");
      const amount = amountValue(paymentAmount);
      if (!Number.isFinite(amount) || amount > paymentTarget.remainingAmountToman) {
        throw new Error("FINANCE_PAYMENT_AMOUNT_INVALID");
      }

      const operation = buildFinancePaymentRecordOperation(operationContext(), {
        paymentId: crypto.randomUUID(),
        transactionId: paymentTarget.id,
        expectedVersion: paymentTarget.domainVersion,
        amountToman: amount,
        paymentMethod,
        paidOn: paymentDate,
        ...(paymentNote.trim() ? { note: paymentNote } : {}),
      });

      setPending(operation);
      setPendingAction("payment");
      setConfirmationParts([
        `پرداخت برای ${paymentTarget.transactionCode}`,
        `مبلغ: ${formatToman(amount)}`,
        `روش: ${financePaymentMethodLabel(paymentMethod)}`,
        `تاریخ: ${paymentDate}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات پرداخت معتبر نیست.");
    }
  }

  function startVoid(transaction: FinanceTransaction) {
    setVoidTarget(transaction);
    setVoidReason("");
    setPaymentTarget(undefined);
    setMessage(undefined);
    setError(undefined);
  }

  function prepareVoid() {
    if (!voidTarget) return;
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWrite) throw new Error("FINANCE_WRITE_PERMISSION_DENIED");

      const operation = buildFinanceTransactionVoidOperation(operationContext(), {
        transactionId: voidTarget.id,
        expectedVersion: voidTarget.domainVersion,
        reason: voidReason,
      });

      setPending(operation);
      setPendingAction("void");
      setConfirmationParts([
        `ابطال ${voidTarget.transactionCode}`,
        `مبلغ: ${formatToman(voidTarget.amountToman)}`,
        `دلیل: ${voidReason.trim()}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "دلیل ابطال معتبر نیست.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const action = pendingAction;
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);
      setPendingAction(undefined);

      if (result.operation.status !== "terminal_conflict") {
        if (action === "counterparty") resetCounterpartyForm();
        if (action === "transaction") resetTransactionForm();
        if (action === "payment") setPaymentTarget(undefined);
        if (action === "void") setVoidTarget(undefined);
      }

      if (result.operation.status === "synced") {
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت مالی انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (loading && snapshot.transactions.length === 0 && snapshot.counterparties.length === 0) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری حسابداری…</p>
      </section>
    );
  }

  const categoryOptions = CATEGORY_OPTIONS[transactionKind];
  const activeCounterparties = snapshot.counterparties.filter((item) => item.isActive);

  return (
    <section className="section-card" aria-labelledby="finance-management-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M6A</p>
          <h2 id="finance-management-title">حسابداری</h2>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={loading}
          onClick={() => void refresh()}
        >
          تازه‌سازی
        </button>
      </div>

      <p className="section-note">
        ثبت فروش، خرید، هزینه و پرداخت از مسیر آفلاین امن انجام می‌شود. رکورد ثبت‌شده حذف فیزیکی
        نمی‌شود و اصلاح اشتباه با ابطال صریح انجام می‌شود.
      </p>

      <div className="manager-grid">
        <article className="manager-card">
          <strong>فروش ثبت‌شده</strong>
          <p>{formatToman(totals.sale)}</p>
        </article>
        <article className="manager-card">
          <strong>خرید ثبت‌شده</strong>
          <p>{formatToman(totals.purchase)}</p>
        </article>
        <article className="manager-card">
          <strong>هزینه ثبت‌شده</strong>
          <p>{formatToman(totals.expense)}</p>
        </article>
        <article className="manager-card">
          <strong>مانده پرداخت‌نشده</strong>
          <p>{formatToman(totals.remaining)}</p>
        </article>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">ثبت مالی</p>
            <h3>فروش / خرید / هزینه</h3>
          </div>
        </div>

        <div className="operation-field-grid">
          <label>
            نوع
            <select
              value={transactionKind}
              onChange={(event) => {
                const next = event.target.value as FinanceTransactionKind;
                setTransactionKind(next);
                setCategoryCode(CATEGORY_OPTIONS[next][0]?.code ?? "");
              }}
            >
              <option value="sale">فروش ماهی</option>
              <option value="purchase">خرید</option>
              <option value="expense">هزینه</option>
            </select>
          </label>
          <label>
            دسته
            <select value={categoryCode} onChange={(event) => setCategoryCode(event.target.value)}>
              {categoryOptions.map((item) => (
                <option value={item.code} key={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            شرح
            <input
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="مثلاً فروش ۱۰ عدد دیسکس"
            />
          </label>
          <label>
            مبلغ کل (تومان)
            <input
              inputMode="numeric"
              value={amountToman}
              onChange={(event) => setAmountToman(event.target.value)}
              placeholder="0"
            />
          </label>
          <label>
            تاریخ
            <input
              type="date"
              value={occurredOn}
              onChange={(event) => setOccurredOn(event.target.value)}
            />
          </label>
          <label>
            طرف حساب
            <select
              value={transactionCounterpartyId}
              onChange={(event) => setTransactionCounterpartyId(event.target.value)}
            >
              <option value="">بدون طرف حساب</option>
              {activeCounterparties.map((item) => (
                <option value={item.id} key={item.id}>
                  {counterpartyName(item)}
                </option>
              ))}
            </select>
          </label>
          <label>
            تعداد / مقدار
            <input
              inputMode="decimal"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="اختیاری"
            />
          </label>
          <label>
            واحد
            <input
              value={unit}
              maxLength={40}
              onChange={(event) => setUnit(event.target.value)}
              placeholder="عدد / گرم / ..."
            />
          </label>
          <label>
            مبلغ پرداخت اولیه
            <input
              inputMode="numeric"
              value={initialPaidAmount}
              onChange={(event) => setInitialPaidAmount(event.target.value)}
              placeholder="0"
            />
          </label>
          <label>
            روش پرداخت اولیه
            <select
              value={initialPaymentMethod}
              onChange={(event) =>
                setInitialPaymentMethod(event.target.value as FinancePaymentMethod)
              }
            >
              <option value="cash">نقدی</option>
              <option value="card_transfer">کارت‌به‌کارت</option>
              <option value="bank_transfer">انتقال بانکی</option>
            </select>
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !canWrite}
          onClick={prepareTransaction}
        >
          بررسی و ثبت
        </button>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">طرف حساب</p>
            <h3>{editingCounterparty ? "ویرایش طرف حساب" : "طرف حساب جدید"}</h3>
          </div>
          {editingCounterparty ? (
            <button type="button" className="text-button" onClick={resetCounterpartyForm}>
              جدید
            </button>
          ) : null}
        </div>

        <div className="operation-field-grid">
          <label>
            نام
            <input value={firstName} onChange={(event) => setFirstName(event.target.value)} />
          </label>
          <label>
            نام خانوادگی
            <input value={lastName} onChange={(event) => setLastName(event.target.value)} />
          </label>
          <label>
            تلفن
            <input
              value={phone}
              maxLength={40}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={isCustomer}
              onChange={(event) => setIsCustomer(event.target.checked)}
            />
            مشتری
          </label>
          <label>
            <input
              type="checkbox"
              checked={isVendor}
              onChange={(event) => setIsVendor(event.target.checked)}
            />
            فروشنده
          </label>
          <label>
            <input
              type="checkbox"
              checked={counterpartyActive}
              onChange={(event) => setCounterpartyActive(event.target.checked)}
            />
            فعال
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !canWrite}
          onClick={prepareCounterparty}
        >
          بررسی و ثبت طرف حساب
        </button>

        <div className="work-list">
          {snapshot.counterparties.slice(0, 20).map((item) => (
            <div className="work-item pending" key={item.id}>
              <span className="work-main">
                <strong>{counterpartyName(item)}</strong>
                <span>
                  {item.phone ?? "بدون تلفن"} · {item.isCustomer ? "مشتری" : ""}
                  {item.isCustomer && item.isVendor ? " / " : ""}
                  {item.isVendor ? "فروشنده" : ""} · {item.isActive ? "فعال" : "غیرفعال"}
                </span>
              </span>
              <button type="button" className="text-button" onClick={() => editCounterparty(item)}>
                ویرایش
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">آخرین ثبت‌ها</p>
            <h3>تراکنش‌ها</h3>
          </div>
          <span className="count-pill">{snapshot.transactions.length}</span>
        </div>

        <div className="work-list">
          {snapshot.transactions.length === 0 ? (
            <p className="empty-state">هنوز تراکنش مالی ثبت نشده است.</p>
          ) : (
            snapshot.transactions.map((transaction) => (
              <div className="work-item pending" key={transaction.id}>
                <span className="work-main">
                  <strong>
                    {transaction.transactionCode} ·{" "}
                    {financeTransactionKindLabel(transaction.transactionKind)} ·{" "}
                    {formatToman(transaction.amountToman)}
                  </strong>
                  <span>
                    {transaction.description} · {transaction.occurredOn} · پرداخت‌شده{" "}
                    {formatToman(transaction.paidAmountToman)} · مانده{" "}
                    {formatToman(transaction.remainingAmountToman)}
                  </span>
                  {transaction.status === "voided" ? (
                    <span>
                      باطل شده{transaction.voidReason ? ` · ${transaction.voidReason}` : ""}
                    </span>
                  ) : null}
                </span>
                {transaction.status === "posted" ? (
                  <span style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {transaction.remainingAmountToman > 0 ? (
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => startPayment(transaction)}
                      >
                        ثبت پرداخت
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => startVoid(transaction)}
                    >
                      ابطال
                    </button>
                  </span>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      {paymentTarget ? (
        <div className="manager-card">
          <div className="section-title-row">
            <h3>ثبت پرداخت برای {paymentTarget.transactionCode}</h3>
            <button
              type="button"
              className="text-button"
              onClick={() => setPaymentTarget(undefined)}
            >
              لغو
            </button>
          </div>
          <p className="section-note">مانده: {formatToman(paymentTarget.remainingAmountToman)}</p>
          <div className="operation-field-grid">
            <label>
              مبلغ (تومان)
              <input
                inputMode="numeric"
                value={paymentAmount}
                onChange={(event) => setPaymentAmount(event.target.value)}
              />
            </label>
            <label>
              روش
              <select
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value as FinancePaymentMethod)}
              >
                <option value="cash">نقدی</option>
                <option value="card_transfer">کارت‌به‌کارت</option>
                <option value="bank_transfer">انتقال بانکی</option>
              </select>
            </label>
            <label>
              تاریخ
              <input
                type="date"
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
              />
            </label>
            <label>
              توضیح
              <input
                value={paymentNote}
                maxLength={1000}
                onChange={(event) => setPaymentNote(event.target.value)}
              />
            </label>
          </div>
          <button
            type="button"
            className="secondary-button"
            disabled={busy || !canWrite}
            onClick={preparePayment}
          >
            بررسی و ثبت پرداخت
          </button>
        </div>
      ) : null}

      {voidTarget ? (
        <div className="manager-card">
          <div className="section-title-row">
            <h3>ابطال {voidTarget.transactionCode}</h3>
            <button type="button" className="text-button" onClick={() => setVoidTarget(undefined)}>
              لغو
            </button>
          </div>
          <label>
            دلیل ابطال
            <input
              value={voidReason}
              maxLength={500}
              onChange={(event) => setVoidReason(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="secondary-button"
            disabled={busy || !canWrite}
            onClick={prepareVoid}
          >
            بررسی ابطال
          </button>
        </div>
      ) : null}

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید ثبت مالی"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => {
            setPending(undefined);
            setPendingAction(undefined);
          }}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
